import { fetchWithDefaults } from '@/lib/http/client';
import type { ConversationLifecycleState } from '@/lib/reasoning/conversationLifecycleState';

interface ReasoningStep {
  id: number;
  step_number: number;
  stage: string;
  content: string;
  is_final: boolean;
  status: string;
  tokens_used?: number;
  processing_time_ms?: number;
  created_at: string;
  created_actions?: number[];
  created_proposals?: Array<{
    id: number;
    title: string;
    resolution: string;
  }>;
}

interface ReasoningChain {
  id: number;
  chain_number: number;
  user_message: string;
  status: string;
  created_at: string;
  steps: ReasoningStep[];
  step_limit_reached?: boolean;
  task_id?: string | null;
  processing_status?: string;
}

interface ConversationDetailsResponse {
  conversation: {
    id: number;
    title: string;
    description: string;
    state: ConversationLifecycleState;
    created_at: string;
    updated_at: string;
  };
  chains: ReasoningChain[];
}

class ProposalTimelineService {
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorData}`);
    }
    return response.json();
  }

  /**
   * Get conversation details including chains and steps
   * Used by ActionVersionTimeline to display discussion history
   */
  async getConversationDetails(
    orgId: string,
    conversationId: number
  ): Promise<ConversationDetailsResponse> {
    const response = await fetchWithDefaults(
      `/api/orgs/${orgId}/reasoning/conversations/${conversationId}/`
    );

    return this.handleResponse<ConversationDetailsResponse>(response);
  }
}

export const proposalTimelineService = new ProposalTimelineService();

// Export types for use in components
export type { ConversationDetailsResponse, ReasoningChain, ReasoningStep };
